import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Send, Eye, Users } from "lucide-react";
import { useApi, useFetch, errMsg } from "../lib/api";
import { useVendors } from "./shared";
import { Button } from "./ui/button";
import { IntroEditor } from "./IntroEditor";
import { BriefingActions } from "./BriefingActions";

const Field = ({ label, children }) => <label className="grid gap-1.5"><span className="text-xs text-slate-400">{label}</span>{children}</label>;

export const EmailPreview = () => {
  const api = useApi();
  const { data: types } = useFetch("/email-preview/types");
  const { data: vendors } = useVendors();
  const { data: employers } = useFetch("/employers");
  const [type, setType] = useState("hold_alert");
  const [org, setOrg] = useState("");
  const [out, setOut] = useState(null);
  const [err, setErr] = useState(null);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState("");
  const [applied, setApplied] = useState({ intro: null, n: 0 });
  const onApply = useCallback((intro) => setApplied((a) => ({ intro, n: a.n + 1 })), []);
  const regenerate = () => setApplied((a) => ({ intro: null, n: a.n + 1, refresh: true }));
  const rewordable = type.startsWith("reminder_");
  const meta = (types || []).find((x) => x.key === type);
  const orgs = meta?.org === "vendor" ? vendors : meta?.org === "employer" ? employers : null;
  const orgOk = !orgs || orgs.some((o) => o.id === org);

  useEffect(() => { if (orgs && !orgs.some((o) => o.id === org)) setOrg(orgs[0]?.id || ""); }, [orgs, org]);
  useEffect(() => {
    if (!meta || (meta.org !== "company" && (!orgs || !orgOk))) return;
    let live = true;
    setOut(null);
    api.get("/email-preview", { params: { type, org_id: orgs ? org : undefined, intro: applied.intro || undefined, refresh: applied.refresh || undefined } })
      .then((r) => { if (live) { setOut(r.data); setErr(null); } })
      .catch((e) => { if (live) { setOut(null); setErr(errMsg(e)); } });
    return () => { live = false; };
  }, [api, type, org, meta, orgs, orgOk, applied]);

  const sendTest = async () => {
    setSending(true);
    try { const r = await api.post("/email-preview/send", { type, org_id: orgs ? org : null, intro: applied.intro }); toast.success(`Test email sent to ${r.data.sent_to}`); }
    catch (e) { toast.error(errMsg(e)); }
    setSending(false);
  };

  return (
    <div className="grid lg:grid-cols-[320px_1fr] gap-6" data-testid="email-preview">
      <div className="c-surface p-5 grid gap-4 content-start">
        <p className="text-sm text-slate-400">See exactly what each email looks like, including the organisation's logo where the email carries one, before it goes out. Previews never email anyone; only the send buttons below do.</p>
        <Field label="Email"><select className="c-input" value={type} onChange={(e) => { setType(e.target.value); setApplied({ intro: null, n: 0 }); }} data-testid="email-preview-type-select">
          {(types || []).map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}</select></Field>
        {orgs && <Field label={meta.org === "vendor" ? "Contractor" : "Principal employer"}>
          <select className="c-input" value={org} onChange={(e) => { setOrg(e.target.value); setApplied({ intro: null, n: 0 }); }} data-testid="email-preview-org-select">
            {orgs.map((o) => <option key={o.id} value={o.id}>{o.vendor_name || o.company_name}</option>)}</select></Field>}
        {meta && <div className="text-xs text-slate-400 flex gap-2" data-testid="email-preview-audience"><Users size={14} className="shrink-0 mt-0.5" />Goes to: {meta.audience}</div>}
        {rewordable && <IntroEditor key={type} type={type} draft={draft} setDraft={setDraft} onApply={onApply} />}
        {type.startsWith("weekly_") && org && orgOk && <BriefingActions canSend={!err} key={type + org} kind={type === "weekly_briefing" ? "briefing" : "nudge"} eid={org} onRegenerate={regenerate} />}
        <Button onClick={sendTest} disabled={!out || sending} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="email-preview-send-test-btn">
          <Send size={14} className="mr-1.5" />{sending ? "Sending…" : "Send test to my inbox"}</Button>
      </div>
      <div className="c-surface p-5 min-w-0">
        {err && <p className={`text-sm ${err.startsWith("Nothing to fix") ? "text-emerald-400" : "text-red-400"}`} data-testid="email-preview-error">{err}</p>}
        {out && <>
          <div className="grid gap-1 text-sm mb-4 border-b border-[#1F2A3E] pb-4">
            <div><span className="text-slate-500 w-20 inline-block">Subject</span><span className="text-white font-medium" data-testid="email-preview-subject">{out.subject}</span></div>
            <div data-testid="email-preview-recipients"><span className="text-slate-500 w-20 inline-block">To</span>
              {out.recipients.length ? <span className="text-slate-300 break-all">{out.recipients.join(", ")}</span>
                : <span className="text-amber-400">No email address on file, so this email would not be sent. Add one in the organisation profile.</span>}</div>
            <div><span className="text-slate-500 w-20 inline-block">Language</span><span className="text-slate-300" data-testid="email-preview-language">{out.language}</span>
              {applied.intro && <span className="ml-3 text-amber-400 text-xs" data-testid="email-preview-draft-flag">Showing unsaved wording</span>}</div>
          </div>
          <iframe title="Email preview" sandbox="" srcDoc={`<body style="margin:0;background:#fff">${out.html}</body>`}
            className="w-full h-[560px] rounded-md bg-white border border-[#1F2A3E]" data-testid="email-preview-frame" />
        </>}
        {!out && !err && <div className="text-sm text-slate-500 flex items-center gap-2"><Eye size={14} />Loading preview… (translations can take a few seconds the first time)</div>}
      </div>
    </div>
  );
};
